import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Observable, of} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {InterviewStatus, TrackerCandidateStatus} from '../../../model';
import {interviewStatusForStatusId, trackerCandidateStatusForId} from '../../../utils';
import {AnalyticsService} from '../../shared/analytics.service';
import {isErrorResponse, isIdentificationResponse, OptionalIdentification} from '../../shared/api.service';
import {AuthService} from '../../shared/auth.service';
import {IdentificationService} from '../../shared/identification.service';
import {IdentifyModalComponent} from '../../shared/identify-modal/identify-modal.component';
import {ModalComponent} from '../../shared/modal/modal.component';
import {ClientApiService} from '../client-api.service';

@Component({
  selector: 'cta',
  templateUrl: './cta.component.html',
  styleUrls: ['./cta.component.scss']
})
export class CtaComponent implements OnInit {

  @Input() submittal: Submittal;
  @Input() staff: StaffInfo[];
  @Input() showReview: boolean;

  @Output() complete = new EventEmitter<void>();

  @ViewChild('interviewModal') interviewModal: ModalComponent;
  @ViewChild('notInterestedModal') notInterestedModal: ModalComponent;
  @ViewChild('extendOfferModal') extendOfferModal: ModalComponent;
  @ViewChild('cancelModal') cancelModal: ModalComponent;
  @ViewChild('reconsiderModal') reconsiderModal: ModalComponent;
  @ViewChild('identifyModal') identifyModal: IdentifyModalComponent;
  identifyCallback: Function | null = null;

  messageText: string | null = null;
  email: string | null = null;
  reason: number | null = null;
  ctaSubmitting = false;
  ctaSubmitted = false;
  error = false;
  masquerading$ = this.Auth.masquerading$;

  NOT_INTERESTED_REASONS: string[] = [
    'The candidate\'s skills aren\'t a match',
    'The rate for the candidate is out of my range',
    'The candidate doesn\'t have the level of experience I need',
    'Other'
  ];

  CANCEL_REASONS: string[] = [
    'Scheduling issues',
    'The role has been filled with another candidate',
    'Shifting priorities',
    'Other'
  ];

  constructor(private Api: ClientApiService,
              private Auth: AuthService,
              private route: ActivatedRoute,
              private Router: Router,
              private Analytics: AnalyticsService,
              private Identification: IdentificationService) {
  }

  ngOnInit() {
    this.resetForm();
  }

  showInterviewModal(): void {
    this.resetForm();
    this.interviewModal.show();
    this.Analytics.trackEvent('Order CTA clicked: Interview', {category: 'Action'});
  }

  showNotInterestedModal(): void {
    this.resetForm();
    this.notInterestedModal.show();
    this.Analytics.trackEvent('Order CTA clicked: Not Interested', {category: 'Action'});
  }

  showExtendOfferModal(): void {
    if (this.Auth.isFullyAuthenticated() && !this.masquerading$.getValue()) {
      this.submitExtendOffer();
    } else {
      this.resetForm();
      this.extendOfferModal.show();
    }
    this.Analytics.trackEvent('Order CTA clicked: Offer Extended', {category: 'Action'});
  }

  showReconsiderModal(): void {
    if (this.Auth.isFullyAuthenticated() && !this.masquerading$.getValue()) {
      this.submitReconsider();
    } else {
      this.resetForm();
      this.reconsiderModal.show();
    }
    this.Analytics.trackEvent('Order CTA clicked: Candidate Reconsidered', {category: 'Action'});
  }

  submitInterview(): void {
    if (this.masquerading$.getValue()) {
      return;
    }
    this.ctaSubmitting = true;
    this.error = false;
    this.Api.scheduleCandidateInterview(this.submittal.orderId, this.submittal.id, this.messageText, this.email)
      .pipe(switchMap(this.identificationPromise(this.email)))
      .subscribe(success => {
        if (!success) {
          this.interviewModal.hide();
          return;
        }
        this.Analytics.trackEvent('Order CTA Succeeded: Interview', {category: 'Action'});
        this.ctaSubmitted = true;
        this.ctaSubmitting = false;
      }, err => {
        console.error('Failed to submit interview CTA', err);
        this.error = true;
        this.Analytics.trackEvent('Order CTA failed: Interview', {category: 'Error'});
        this.ctaSubmitting = false;
      });
    this.Analytics.trackEvent('Order CTA submitted: Interview', {category: 'Action'});
  }

  submitExtendOffer(): void {
    if (this.masquerading$.getValue()) {
      return;
    }
    this.ctaSubmitting = true;
    this.error = false;

    if (!this.extendOfferModal.visible$.getValue()) {
      this.extendOfferModal.show();
    }

    this.Api.hireCandidate(this.submittal.orderId, this.submittal.id, this.messageText, this.email)
      .pipe(switchMap(this.identificationPromise(this.email)))
      .subscribe(success => {
        if (!success) {
          this.extendOfferModal.hide();
          return;
        }
        this.Analytics.trackEvent('Order CTA Succeeded: Offer Extended', {category: 'Action'});
        this.ctaSubmitted = true;
        this.ctaSubmitting = false;
      }, err => {
        console.error('Failed to submit extend offer CTA', err);
        this.error = true;
        this.Analytics.trackEvent('Order CTA failed: Offer Extended', {category: 'Error'});
        this.ctaSubmitting = false;
      });
    this.Analytics.trackEvent('Order CTA submitted: Offer Extended', {category: 'Action'});
  }

  submitReconsider(): void {
    if (this.masquerading$.getValue()) {
      return;
    }
    this.ctaSubmitting = true;
    this.error = false;
    this.Api.reconsiderCandidate(this.submittal.orderId, this.submittal.id, this.messageText, this.email)
      .pipe(switchMap(this.identificationPromise(this.email)))
      .subscribe(success => {
        if (!success) {
          this.reconsiderModal.hide();
          return;
        }
        this.Analytics.trackEvent('Order CTA Succeeded: Candidate Reconsidered', {category: 'Action'});
        this.ctaSubmitted = true;
        this.ctaSubmitting = false;
        this.onModalClose();
      }, () => {
        this.error = true;
        this.Analytics.trackEvent('Order CTA failed: Candidate Reconsidered', {category: 'Error'});
        this.ctaSubmitting = false;
      });
    this.Analytics.trackEvent('Order CTA submitted: Candidate Reconsidered', {category: 'Action'});
  }

  submitNotInterested(): void {
    if (this.masquerading$.getValue()) {
      return;
    }
    this.ctaSubmitting = true;
    this.error = false;
    this.Api.passCandidate(this.submittal.orderId, this.submittal.id, this.messageText,
      this.email, this.NOT_INTERESTED_REASONS[this.reason])
      .pipe(switchMap(this.identificationPromise(this.email)))
      .subscribe(success => {
        if (!success) {
          this.notInterestedModal.hide();
          return;
        }
        this.Analytics.trackEvent('Order CTA Succeeded: Not Interested', {category: 'Action'});
        this.ctaSubmitted = true;
        this.ctaSubmitting = false;
      }, () => {
        this.error = true;
        this.Analytics.trackEvent('Order CTA failed: Not Interested', {category: 'Error'});
        this.ctaSubmitting = false;
      });
    this.Analytics.trackEvent('Order CTA submitted: Not Interested', {category: 'Action'});
  }

  onModalClose(): void {
    if (this.ctaSubmitted && this.Auth.isFullyAuthenticated()) {
      this.complete.emit();
    }
  }

  showCancelModal(): void {
    this.resetForm();
    this.cancelModal.show();
    this.Analytics.trackEvent('Order CTA clicked: Cancel', {category: 'Action'});
  }

  submitCancel(): void {
    if (this.masquerading$.getValue()) {
      return;
    }
    this.ctaSubmitting = true;
    this.error = false;
    this.Api.cancelCandidate(this.submittal.orderId, this.submittal.id, this.messageText, this.email, this.CANCEL_REASONS[this.reason])
      .pipe(switchMap(this.identificationPromise(this.email)))
      .subscribe(success => {
        if (!success) {
          this.cancelModal.hide();
          return;
        }
        this.Analytics.trackEvent('Order CTA Succeeded: Cancel', {category: 'Action'});
        this.ctaSubmitted = true;
        this.ctaSubmitting = false;
      }, () => {
        this.error = true;
        this.Analytics.trackEvent('Order CTA failed: Cancel', {category: 'Error'});
        this.ctaSubmitting = false;
      });
    this.Analytics.trackEvent('Order CTA submitted: Cancel', {category: 'Action'});
  }

  getLoggedIn(): boolean {
    return this.Auth.isFullyAuthenticated();
  }

  showInterviewButton(): boolean {
    const status = trackerCandidateStatusForId(this.submittal.statusId);
    return status === TrackerCandidateStatus.reviewed ||
      (status === TrackerCandidateStatus.interviewing && interviewStatusForStatusId(this.submittal.statusId) === InterviewStatus.complete)
      || (status === TrackerCandidateStatus.new && !this.showReview);
  }

  showNotInterestedButton(): boolean {
    const status = trackerCandidateStatusForId(this.submittal.statusId);
    return status === TrackerCandidateStatus.new
      || status === TrackerCandidateStatus.reviewed
      || status === TrackerCandidateStatus.interviewing;
  }

  showCancelInterviewButton(): boolean {
    return (trackerCandidateStatusForId(this.submittal.statusId) === TrackerCandidateStatus.interviewing &&
      interviewStatusForStatusId(this.submittal.statusId) !== InterviewStatus.complete);
  }

  showInterviewAgainButton(): boolean {
    return false;
  }

  showExtendOfferButton(): boolean {
    return trackerCandidateStatusForId(this.submittal.statusId) === TrackerCandidateStatus.interviewing;
  }

  showContactAgentButton(): boolean {
    return trackerCandidateStatusForId(this.submittal.statusId) === TrackerCandidateStatus.offerExtended && !!this.staff;
  }

  showReconsiderButton(): boolean {
    return trackerCandidateStatusForId(this.submittal.statusId) === TrackerCandidateStatus.notInterested;
  }

  showReviewButton(): boolean {
    return trackerCandidateStatusForId(this.submittal.statusId) === TrackerCandidateStatus.new && this.showReview;
  }

  goToProfile() {
    const returnTo = this.route.snapshot.queryParams['returnTo'];

    this.Analytics.trackEvent('Order CTA clicked: Review', {category: 'Action'});
    this.Router.navigate(['positions', this.submittal.orderId, 'candidates', this.submittal.id], {
      queryParams: {
        review: true,
        returnTo: returnTo && returnTo.split(',').concat('candidates').join(',')
      }
    });
  }

  isInterviewValid(): boolean {
    if (this.Auth.isFullyAuthenticated()) {
      return this.messageText !== null;
    } else {
      return this.messageText !== null && this.email !== null;
    }
  }

  isNotInterestedValid(): boolean {
    if (this.Auth.isFullyAuthenticated()) {
      return this.reason !== null;
    } else {
      return this.reason !== null && this.email !== null;
    }
  }

  isCancelValid(): boolean {
    if (this.Auth.isFullyAuthenticated()) {
      return this.reason !== null;
    } else {
      return this.reason !== null && this.email !== null;
    }
  }

  resetForm(): void {
    this.ctaSubmitted = false;
    this.ctaSubmitting = false;
    this.error = false;
    this.messageText = null;
    this.email = null;
  }

  private identificationPromise<T>(email?: string): (response: OptionalIdentification<T>) => Observable<T> {
    return (response: OptionalIdentification<T>): Observable<T> => {
      this.Identification.handleResponse(email, response);
      if (isIdentificationResponse(response) || isErrorResponse(response)) {
        return Observable.create(observer => {
          this.identifyCallback = success => {
            this.identifyCallback = null;
            if (success && isIdentificationResponse(response)) {
              observer.next(response.response);
            } else {
              observer.next(success);
            }
          };
          this.identifyModal.show();
        });
      } else {
        return of(<T> response);
      }
    };
  }
}
